import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, IconButton, Button, CircularProgress,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../constants";
import { loyaltyFetch } from "../hooks/loyaltyFetch";

const PAGE_SIZE = 20;

export default function LoyaltyCustomersListDialog({ open, onClose, brandName }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [items, setItems] = useState([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(0);

    const fetchPage = async (pageToLoad) => {
        if (!brandName) return;
        setLoading(true);
        setError("");
        try {
            const offset = pageToLoad * PAGE_SIZE;
            const res = await loyaltyFetch(
                `${LOYALTY_API_BASE}/customers?limit=${PAGE_SIZE}&offset=${offset}`,
                { headers: { "X-Brand": brandName } }
            );
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            const data = await res.json();
            setItems(Array.isArray(data?.items) ? data.items : []);
            setCount(Number(data?.count || 0));
            setPage(pageToLoad);
        } catch (err) {
            setError(err.message || "Erreur lors du chargement des clients");
            setItems([]);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && brandName) {
            fetchPage(0);
        }
    }, [open, brandName]);

    const canPrev = page > 0;
    const canNext = (page + 1) * PAGE_SIZE < count;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "1px solid #e8ecf1", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <FormatListBulletedIcon sx={{ color: SECTION_COLORS.search.main }} />
                    <Typography fontWeight={700} fontFamily="Montserrat, sans-serif">
                        Liste des clients fidélité
                    </Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={28} />
                    </Box>
                ) : error ? (
                    <Box py={2}>
                        <Typography color="error" fontSize={13}>{error}</Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e8ecf1", borderRadius: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: "#f8f9ff" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Profile ID</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Niveau</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Points</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Créé le</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary" }}>
                                            Aucun client trouvé.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map((row) => (
                                        <TableRow key={row.id} hover>
                                            <TableCell>{row.profile_id || "—"}</TableCell>
                                            <TableCell>{row.loyalty_status_name || row.loyalty_status || "—"}</TableCell>
                                            <TableCell>{row.points_balance ?? 0}</TableCell>
                                            <TableCell>{row.created_at ? new Date(row.created_at).toLocaleDateString("fr-FR") : "—"}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
                <Typography fontSize={12} color="text.secondary">Total: {count}</Typography>
                <Box display="flex" gap={1}>
                    <Button onClick={() => fetchPage(page - 1)} disabled={!canPrev || loading}>Précédent</Button>
                    <Button onClick={() => fetchPage(page + 1)} disabled={!canNext || loading}>Suivant</Button>
                    <Button variant="contained" onClick={onClose}>Fermer</Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
